import { message } from "antd";
import { useCallback, useEffect, useState } from "react";

import { updateBusinessEntity } from "@/api/entities";
import type { BusinessEntityDetailVO } from "@/api/entities/type";

/** 可编辑字段 key（与接口 BusinessEntityUpdateRequest 对齐） */
export const ENTITY_EDITABLE_FIELD = "licenseFileUrl" as const;

export type EntityEditableFieldKey = typeof ENTITY_EDITABLE_FIELD;

/** useEntityEdit 入参 */
export interface UseEntityEditOptions {
  /** 工商实体 ID */
  entityId?: string;
  /** 弹窗是否打开 */
  open?: boolean;
  /** 当前详情数据 */
  detail?: BusinessEntityDetailVO | null;
  /** 保存成功后写入详情 */
  applyDetail?: (detail: BusinessEntityDetailVO) => void;
}

/**
 * 工商实体编辑态：进入编辑、双击字段改值、保存/取消
 */
export const useEntityEdit = ({ entityId, open, detail, applyDetail }: UseEntityEditOptions) => {
  /** 是否处于编辑模式 */
  const [editMode, setEditMode] = useState(false);

  /** 编辑中的证照 URL 草稿 */
  const [licenseFileUrlDraft, setLicenseFileUrlDraft] = useState("");
  /** 保存请求中 */
  const [saving, setSaving] = useState(false);

  /** 弹窗关闭时重置编辑态 */
  useEffect(() => {
    if (open) return;
    setEditMode(false);
    setLicenseFileUrlDraft("");
    setSaving(false);
  }, [open]);

  /** 进入编辑模式，初始化草稿 */
  const enterEditMode = useCallback(() => {
    setLicenseFileUrlDraft(detail?.licenseFileUrl ?? "");
    setEditMode(true);
  }, [detail?.licenseFileUrl]);

  /** 取消编辑，恢复详情原值 */
  const cancelEdit = useCallback(() => {
    setEditMode(false);
    setLicenseFileUrlDraft(detail?.licenseFileUrl ?? "");
  }, [detail?.licenseFileUrl]);

  /** 读取字段草稿或详情原值 */
  const getFieldValue = useCallback(
    (fieldKey: EntityEditableFieldKey): string => {
      if (fieldKey === ENTITY_EDITABLE_FIELD) {
        return editMode ? licenseFileUrlDraft : (detail?.licenseFileUrl ?? "");
      }
      return "";
    },
    [detail?.licenseFileUrl, editMode, licenseFileUrlDraft],
  );

  /** 更新证照 URL 草稿（上传/移除附件时调用） */
  const updateLicenseFileUrl = useCallback((url: string) => {
    setLicenseFileUrlDraft(url);
  }, []);

  /** 提交保存 */
  const saveEdit = useCallback(async () => {
    if (!entityId) return;
    setSaving(true);
    try {
      const trimmed = licenseFileUrlDraft.trim();
      const res = await updateBusinessEntity(entityId, {
        licenseFileUrl: trimmed === "" ? null : trimmed,
      });
      if (res.data) {
        applyDetail?.(res.data);
      }
      message.success("保存成功");
      setEditMode(false);
    } catch {
      message.error("保存失败，请稍后重试");
    } finally {
      setSaving(false);
    }
  }, [applyDetail, entityId, licenseFileUrlDraft]);

  return {
    editMode,
    saving,
    enterEditMode,
    cancelEdit,
    saveEdit,
    getFieldValue,
    updateLicenseFileUrl,
  };
};
